import AsyncStorage from '@react-native-async-storage/async-storage';
import aes from 'crypto-js/aes'
import { encryptCache } from 'utils/cache/encrypt-cache'
import encUtf8 from "crypto-js/enc-utf8";
import HelperDevelopment from "utils/helper-development";
import { appRestart } from 'utils/helper-restart'
import HelperFile from 'utils/HelperFile'
import HelperValidate from 'utils/helper-validate'
import _ from 'lodash'
import HelperDate, { dateFormat } from 'utils/helper-date'

const CacheStorage = {
	
	constructed: false,
	key: 'aidja0ije234ij2340ij234oi234oinroinasd0ndoinqwoinoi23n4oi23noi234noin2roiandoidas3',
	encrypt: !HelperDevelopment.isDev() && !HelperDevelopment.isTestWeb(),
	cachePrefix: '@cache_',

	storageData: {},
	savingToDevice: false,
	activeSave: null,
	clearing: false,
	clearPromise: null,
	generation: 0,
	savingToDeviceTimeout: null,
	pendingSaveKeys: new Set(),
	pendingDeleteKeys: new Set(),
	saveFailures: 0,
	maxSaveRetries: 5,
	lastSaveError: null,
	lastTouchWrites: {},
	touchWriteIntervalMs: 60 * 1000,
	cacheIsEmpty: true,
	legacyStorageKey: '@cache_mod',
	
	construct: () => {
		if( CacheStorage.constructed === false ) {
			CacheStorage.constructed = true;
		}
	},
	
	reset: () => {
		CacheStorage.generation += 1;
		CacheStorage.constructed = false;
		CacheStorage.storageData = {};
		CacheStorage.pendingSaveKeys.clear();
		CacheStorage.pendingDeleteKeys.clear();
		CacheStorage.saveFailures = 0;
		CacheStorage.lastSaveError = null;
		CacheStorage.lastTouchWrites = {};
		CacheStorage.cacheIsEmpty = true;
		clearTimeout( CacheStorage.savingToDeviceTimeout );
		CacheStorage.savingToDeviceTimeout = null;
	},
	
	isCacheEmpty: () => {
		return CacheStorage.cacheIsEmpty;
	},
	
	createCacheKey: ( key ) => {
		return `${CacheStorage.cachePrefix}${ key }`;
	},
	
	getFromDevice: async () => {
		if( CacheStorage.clearing ) return;
		const generation = CacheStorage.generation;
		CacheStorage.storageData = {};
		CacheStorage.cacheIsEmpty = true;

		const keys = await AsyncStorage.getAllKeys();
		const cacheKeys = keys.filter( key => {
			return key.startsWith( CacheStorage.cachePrefix ) && key !== CacheStorage.legacyStorageKey;
		});

		if( cacheKeys.length > 0 ) {
			const entries = await AsyncStorage.multiGet( cacheKeys );
			if( generation !== CacheStorage.generation ) return;
			entries.forEach( ([ key, value ]) => {
				const parsedValue = CacheStorage.parseStoredValue( value );
				if( parsedValue !== null ) {
					CacheStorage.storageData[ key ] = parsedValue;
				}
			});
			CacheStorage.cacheIsEmpty = Object.keys( CacheStorage.storageData ).length === 0;
			return;
		}

		// One-time migration from the original all-data-in-one-key cache.
		const legacyValue = await AsyncStorage.getItem( CacheStorage.legacyStorageKey );
		if( generation !== CacheStorage.generation ) return;
		const legacyData = CacheStorage.parseStoredValue( legacyValue );
		if( legacyData !== null ) {
			CacheStorage.storageData = legacyData;
			CacheStorage.cacheIsEmpty = Object.keys( legacyData ).length === 0;
			Object.keys( legacyData ).forEach( key => {
				return CacheStorage.pendingSaveKeys.add(key);
			} );
			CacheStorage.scheduleSaveToDevice();
		}
	},

	parseStoredValue: value => {
		if( value === null ) return null;

		let jsonValue = value;
		if( CacheStorage.encrypt ) {
			jsonValue = CacheStorage.decode( jsonValue );
		}

		if( jsonValue === null || jsonValue === '' ) return null;

		try {
			return JSON.parse( jsonValue );
		} catch ( error ) {
			console.warn( 'JSON PARSE', error );
			return null;
		}
	},

	serializeStoredValue: value => {
		const jsonValue = JSON.stringify( value );
		return CacheStorage.encrypt ? CacheStorage.encode( jsonValue ) : jsonValue;
	},

	scheduleSaveToDevice: () => {
		if( CacheStorage.clearing || CacheStorage.saveFailures >= CacheStorage.maxSaveRetries ) return;
		clearTimeout( CacheStorage.savingToDeviceTimeout );
		CacheStorage.savingToDeviceTimeout = setTimeout( CacheStorage.saveToDevice, CacheStorage.saveFailures === 0 ? 250 : Math.min( 60000, 1000 * 2 ** ( CacheStorage.saveFailures - 1 ) ) );
	},

	saveToDevice: () => {
		if( CacheStorage.clearing ) return Promise.resolve();
		if( CacheStorage.activeSave ) return CacheStorage.activeSave;
		CacheStorage.activeSave = Promise.resolve().then( CacheStorage.performSaveToDevice ).finally( () => {
			CacheStorage.activeSave = null;
		});
		return CacheStorage.activeSave;
	},

	performSaveToDevice: async () => {
		if( CacheStorage.clearing ) return;
		if( CacheStorage.savingToDevice ){
			CacheStorage.scheduleSaveToDevice();
			return;
		}

		clearTimeout( CacheStorage.savingToDeviceTimeout );
		CacheStorage.savingToDeviceTimeout = null;

		const keys = Array.from( CacheStorage.pendingSaveKeys );
		const deletedKeys = Array.from( CacheStorage.pendingDeleteKeys );
		if( keys.length === 0 && deletedKeys.length === 0 ) return;

		CacheStorage.pendingSaveKeys.clear();
		CacheStorage.pendingDeleteKeys.clear();
		CacheStorage.savingToDevice = true;

		try {
			const entries = keys
				.filter( key => {
					return key in CacheStorage.storageData;
				} )
				.map( key => {
					return [key, CacheStorage.serializeStoredValue(CacheStorage.storageData[key])];
				} );

			if( entries.length > 0 ) {
				await AsyncStorage.multiSet( entries );
			}

			if( deletedKeys.length > 0 ) await AsyncStorage.multiRemove( deletedKeys );
			await AsyncStorage.removeItem( CacheStorage.legacyStorageKey );
			CacheStorage.saveFailures = 0;
			CacheStorage.lastSaveError = null;
		} catch( error ) {
			if( !CacheStorage.clearing ) {
				CacheStorage.saveFailures += 1;
				CacheStorage.lastSaveError = error;
				keys.forEach( key => {
					if( key in CacheStorage.storageData ) CacheStorage.pendingSaveKeys.add( key );
				});
				deletedKeys.forEach( key => {
					if( !(key in CacheStorage.storageData) ) CacheStorage.pendingDeleteKeys.add( key );
				});
			}
		} finally {
			CacheStorage.savingToDevice = false;
			if( CacheStorage.pendingSaveKeys.size > 0 || CacheStorage.pendingDeleteKeys.size > 0 ) {
				CacheStorage.scheduleSaveToDevice();
			}
		}
	},
	
	storeData: async ( key, data, { updated_at = null } = {} ) => {
		if( CacheStorage.clearing ) return;
		CacheStorage.construct();

		try {
			CacheStorage.storageData[ CacheStorage.createCacheKey( key ) ] = {
				updated_at: updated_at ?? HelperDate.date().format( dateFormat.DATE_TIME ),
				touched_at: HelperDate.date().format( dateFormat.DATE_TIME ),
				data: data
			};
			
			const cacheKey = CacheStorage.createCacheKey( key );
			CacheStorage.pendingDeleteKeys.delete( cacheKey );
			CacheStorage.saveFailures = 0;
			CacheStorage.lastTouchWrites[ cacheKey ] = Date.now();
			CacheStorage.pendingSaveKeys.add( cacheKey );
			CacheStorage.cacheIsEmpty = false;
			CacheStorage.scheduleSaveToDevice();
		} catch (e) {
			// saving error
		}
	},

	getData: async ( key ) => {
		CacheStorage.construct();
		
		try {
			if( CacheStorage.createCacheKey( key ) in CacheStorage.storageData ) {
				return CacheStorage.storageData[ CacheStorage.createCacheKey( key ) ];
			}
			
			return null;
		} catch(e) {}

		return null;
	},

	getDataKeys: () => {
		return Object.keys( CacheStorage.storageData );
	},
	
	touchData: async ( key ) => {
		if( CacheStorage.clearing ) return;
		const cacheKey = CacheStorage.createCacheKey( key );
		const entry = CacheStorage.storageData[ cacheKey ];
		if( !entry ) return;
		entry.touched_at = HelperDate.date().format( dateFormat.DATE_TIME );
		const now = Date.now();
		if( now - (CacheStorage.lastTouchWrites[ cacheKey ] ?? -Infinity) < CacheStorage.touchWriteIntervalMs ) return;
		CacheStorage.lastTouchWrites[ cacheKey ] = now;
		CacheStorage.pendingSaveKeys.add( cacheKey );
		CacheStorage.scheduleSaveToDevice();
	},

	removeData: async ( key ) => {
		if( CacheStorage.clearing ) return CacheStorage.clearPromise;
		const cacheKey = CacheStorage.createCacheKey( key );
		delete CacheStorage.storageData[ cacheKey ];
		delete CacheStorage.lastTouchWrites[ cacheKey ];
		CacheStorage.pendingSaveKeys.delete( cacheKey );
		CacheStorage.pendingDeleteKeys.add( cacheKey );
		CacheStorage.cacheIsEmpty = Object.keys( CacheStorage.storageData ).length === 0;
		// A write already in progress must finish before the deletion is persisted.
		await CacheStorage.activeSave;
		await CacheStorage.saveToDevice();
		if( CacheStorage.lastSaveError ) throw CacheStorage.lastSaveError;
	},

	clearCache: ( { restart = true } = {} ) => {
		if( CacheStorage.clearPromise ) return CacheStorage.clearPromise;
		CacheStorage.clearing = true;
		CacheStorage.reset();
		const activeSave = CacheStorage.activeSave;
		CacheStorage.clearPromise = ( async () => {
			// Enumerate only after the active write finishes, including any new keys it created.
			await activeSave;
			const keys = await CacheStorage.getCacheKeys();
			await AsyncStorage.multiRemove( keys );
			if( restart ) appRestart();
		})().finally( () => {
			CacheStorage.clearing = false;
			CacheStorage.clearPromise = null;
		});
		return CacheStorage.clearPromise;
	},

	getCacheKeys: async () => {
		let keys = await AsyncStorage.getAllKeys();

		let return_keys = [];

		keys.forEach( ( key ) => {
			if( HelperValidate.isValid( key ) ) {
				if( _.includes( key, CacheStorage.cachePrefix ) ) {
					return_keys.push( key.toString() );
				}
			}
		})

		return return_keys;
	},
	encode: ( data ) => {
		return encryptCache( data, CacheStorage.key );
	},

	decode: ( data ) => {
		try{
			const bytes  = aes.decrypt( data , CacheStorage.key );
			return bytes.toString( encUtf8 );
		}catch( e ){
			return null;
		}
	},

	writeToConsole: async () => {
		console.prefix( `this::writeToConsole CacheStorage.storageData`, CacheStorage.storageData );
	},

	calculateCacheStorage: async ( makeReadable ) => {
		let keys = await CacheStorage.getCacheKeys();
		let byteSize = 0;

		for( let key of keys ){
			let jsonValue = await AsyncStorage.getItem( key );

			if( jsonValue !== null ) {
				byteSize = byteSize + HelperFile.byteLength( jsonValue );
			}
		}

		return makeReadable ? HelperFile.formatBytes( byteSize ): byteSize;
	},
}

export default CacheStorage
